<template>
  <div>
    <v-layout row wrap>
      <v-flex xs12 md6>
        <h3>Assignés</h3>
        <v-list>
          <template v-for="item in assignees">
            <FormLine
              :item="item"
              v-model="project.assignees"
              :key="item.name"
              :edit-mode="editMode"
            ></FormLine>
            <v-divider :key="`${item.name}-divider`" />
          </template>
        </v-list>
        <h3>Etat</h3>
        <FormLine
          :item="{
            name: 'naturalSubscription',
            label: 'Appel entrant',
            readOnly: true,
            value: () => (this.project.naturalSubscription ? 'Oui' : 'Non')
          }"
          v-model="project"
          :edit-mode="false"
        />
        <v-list>
          <FormLine
            :item="{
              items: progresses,
              value: () => project.currentProgress,
              as: 'select',
              name: 'progress',
              label: 'Colonne',
              update: onProgressUpdate
            }"
            :edit-mode="editMode"
          />
          <v-list-tile class="form-line-global-item">
            <v-list-tile-content class="align-end">
              <v-checkbox
                v-model="project.didAllVisits"
                label="Toutes visites"
              />
            </v-list-tile-content>
          </v-list-tile>
        </v-list>

        <template v-if="groupedVisits && Object.keys(groupedVisits).length">
          <h3>Visites</h3>

          <div class="pl-3 pr-3">
            <p v-if="isBeforeSentToAL" class="mt-2 mb-0" style="text-align: center">
              ⚠️ Ceci est un état prévisionnel qui sera figé une fois passé à
              "Envoyé à AL"
            </p>
            <template v-for="(visits, group) in groupedVisits">
              <v-subheader :key="`header-${group}`" class="mb-0">{{
                group
              }}</v-subheader>
              <dl :key="`list-${group}`" class="pa-0 visits">
                <template v-for="(item, i) in visits">
                  <dd :key="`dd-${i}`">{{ item.target }}</dd>
                  <dt :key="`dt-${i}`">{{ item.amount }} €</dt>
                </template>
              </dl>
            </template>
          </div>
        </template>
      </v-flex>
      <v-flex xs12 md6 v-if="isSuperAdmin" class="admin-billing">
        <h3>Facturation</h3>
        <v-alert
          type="error"
          :value="project.dateMove === null"
          class="mr-2 mt-2"
        >
          Pas de date d'emménagement, la facture ne sera pas générée
        </v-alert>
        <template v-for="(fields, step) in billing">
          <h4 :key="`header-${step}`">{{ step }}</h4>
          <v-list :key="`list-${step}`">
            <template v-for="item in fields">
              <FormLine
                :key="item.name"
                :item="item"
                :edit-mode="editMode"
              ></FormLine>
              <v-divider :key="`${item.name}-divider`" />
            </template>
          </v-list>
        </template>
      </v-flex>
      <v-flex xs12 md6 v-else>
        <v-alert type="info" :value="true" class="mr-2 mt-2">
          La facturation sera disponible après l'envoi à Action Logement
        </v-alert>
      </v-flex>
    </v-layout>
    <v-layout row v-if="isSuperAdmin">
      <v-flex xs12 md6>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn color="red" v-on="on"
              >Supprimer définitivement le projet</v-btn
            >
          </template>
          <v-list>
            <v-list-tile>
              <v-list-tile-title>Etes vous sûr ? </v-list-tile-title>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-title @click="deleteProject()"
                >Oui</v-list-tile-title
              >
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-title>Non</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-btn color="red" @click="refresh">Rafraichir</v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn color="red" v-on="on">Activer mot de passe maitre</v-btn>
          </template>
          <v-list>
            <v-list-tile>
              <v-list-tile-title>Etes vous sûr ? </v-list-tile-title>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-title @click="enableMasterPassword()"
                >Oui</v-list-tile-title
              >
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-title>Non</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import * as _ from "lodash";
import { roleToHumanReadable } from "@/data/leaseData";
import FormLine from "../commons/FormLine";
import { fetchHil } from "../../utils";
import {
  ALL_COLUMNS,
  COLUMNS_NAMES,
  PROGRESS_ORDER,
  VISIT_AUTHORS
} from "../../data/DefaultSettings";
import { mapGetters, mapState } from "vuex";

export default {
  props: ["project", "proposals", "editMode"],
  components: { FormLine },
  methods: {
    async deleteProject() {
      try {
        const resp = await fetchHil(`/lease/${this.project._id}`, {
          method: "DELETE"
        });
        if (!resp.ok) {
          return alert("Une erreur a eu lieu");
        }
        this.$router.push("/reloc");
      } catch (e) {
        alert("Une erreur a eu lieu");
      }
    },
    async refresh() {
      try {
        const resp = await fetchHil(`/lease/${this.project._id}/refresh`, {
          method: "GET"
        });
        if (!resp.ok) {
          return alert("Une erreur a eu lieu");
        }
        window.location.reload();
      } catch (e) {
        alert("Une erreur a eu lieu");
      }
    },
    async enableMasterPassword() {
      try {
        const resp = await fetchHil(`/addMasterPassword`, {
          method: "POST",
          body: { target: this.project.userId }
        });
        if (!resp.ok) {
          return alert("Une erreur a eu lieu");
        }
        console.log(await resp.text());
      } catch (e) {
        alert("Une erreur a eu lieu");
      }
      alert("ok");
    },
    onProgressUpdate(step) {
      this.project.currentProgress = step;
      this.$forceUpdate();
    }
  },
  computed: {
    ...mapGetters("user", ["isSuperAdmin"]),
    ...mapState("user", ["rewards"]),
    ...mapState("autocompletes", {
      staffByTrigram: state => _.keyBy(state.staff, "trigram")
    }),

    billing() {
      return Object.assign(
        {},
        ...["sentToAL", "paid"].map(step => ({
          [COLUMNS_NAMES[step]]: [
            "prescriptor",
            "lead",
            "sale",
            "dispatcher",
            "reloc",
            "alsender"
          ].map(field => {
            const roleUser = this.project.assignees[field]
              ? this.staffByTrigram[this.project.assignees[field]]
              : null;
            const hint = roleUser ? roleUser.rewards?.[step]?.[field] : null;
            return {
              name: `${step}-${field}`,
              label:
                field === "prescriptor"
                  ? "Prescripteur"
                  : roleToHumanReadable(field),
              // This is a number, but input type number misbehaves when text is entered,
              // so let's keep it as text and let validation do its job
              as: "text",
              hint:
                typeof hint === "number"
                  ? `Valeur actuelle de ${[
                      roleUser.firstName,
                      roleUser.lastName
                    ].join(" ")}: ${hint}`
                  : undefined,
              validation: "numeric|min_value:0",
              value: () => this.project.billingOverrides?.[step]?.[field],
              update: value => {
                _.set(
                  this.project,
                  `billingOverrides.${step}.${field}`,
                  value === "" ? null : +value
                );
              },
              suffix: "€"
            };
          })
        }))
      );
    },

    isBeforeSentToAL() {
      const idx = PROGRESS_ORDER.indexOf(this.project.currentProgress);
      return idx < PROGRESS_ORDER.indexOf("sentToAL");
    },

    groupedVisits() {
      const groupVisits = visits => {
        const toFullType = type =>
          type ? _.find(VISIT_AUTHORS, { value: type }).text : "Inconnu";
        return _.groupBy(visits, ({ type }) => toFullType(type));
      };
      if (this.isBeforeSentToAL) {
        // Simulate what's going to happen when we transition to sentToAL, this is almost the same code as what's in the lease model
        return groupVisits(
          _(this.proposals)
            .filter("visited")
            .flatMap(({ visited }) => {
              if (visited.author === "operator") {
                return [
                  {
                    target: this.project.assignees.reloc,
                    type: "operator",
                    amount: 30,
                    date: new Date()
                  }
                ];
              } else if (visited.author === "sale") {
                return [
                  {
                    target: this.project.assignees.sale,
                    type: "sale",
                    amount: 30,
                    date: new Date()
                  }
                ];
              } else if (
                visited.author === "other" &&
                visited.authorOther != null &&
                visited.authorOther != ""
              ) {
                return {
                  target: visited.authorOther,
                  type: "other",
                  amount: 30,
                  date: new Date()
                };
              } else {
                return [];
              }
            })
            .value()
        );
      } else {
        if (this.project.billing?.visits) {
          return groupVisits(this.project.billing.visits);
        }
      }

      return null;
    },

    progresses() {
      const progressIndex = PROGRESS_ORDER.indexOf(this.project.currentProgress);
      const shouldFilterProgresses =
        progressIndex >= PROGRESS_ORDER.indexOf("sentToAL");
      const columns = _(ALL_COLUMNS)
        .reject({ isAgregate: true })
        .map((colDef, i) => ({
          ...colDef,
          text: `[${i < 10 ? `0${i}` : i}] ${colDef.text}`
        }))
        .filter(colDef => {
          if (shouldFilterProgresses) {
            if (PROGRESS_ORDER.indexOf(colDef.value) < progressIndex) {
              return false;
            }
          }
          return true;
        })
        .value();
      if (this.isSuperAdmin) {
        return columns;
      } else {
        return columns.filter(({ superAdminOnly }) => !superAdminOnly);
      }
    }
  },
  data() {
    return {
      assignees: [
        {
          name: "prescriptor",
          label: "Prescripteur",
          linkValue: () => {
            const prescriptor = this.project.assignees?.prescriptor;
            return prescriptor ? `/prospect/${prescriptor}` : null;
          },
          validation: {
            required: false,
            regex: /^[0-9a-f]{24}$/i
          }
        },
        {
          name: "lead",
          label: "Lead",
          as: "staff"
        },
        {
          name: "sale",
          label: "Commercial",
          as: "staff"
        },
        {
          name: "dispatcher",
          label: "Répartiteur",
          as: "staff"
        },
        {
          name: "reloc",
          label: "Relocation",
          as: "staff"
        },
        {
          name: "alsender",
          label: "Dossier final",
          as: "staff"
        }
      ]
    };
  }
};
</script>
<style scoped>
.admin-billing >>> .v-messages {
  display: unset !important;
}

.visits {
  display: grid;
  grid-template-columns: auto auto;
  grid-auto-rows: 1fr;
  grid-auto-flow: row;
}

.visits dt {
  text-align: right;
}
</style>
